import {
  INTEGRATOR_SUBAPP_KEYS,
  INTEGRATOR_SUBAPP_KEYS_SET,
  type IntegratorSubappKey,
} from "@hub-modules";

import { NAV_BY_APP } from "./appNavigation";
import { normalizeQuickNavPath } from "./quickSwitcherPageRank";

function isIntegratorSubappKey(value: string): value is IntegratorSubappKey {
  return INTEGRATOR_SUBAPP_KEYS_SET.has(value);
}

/**
 * Modules that list this path (or a prefix of it) as a tab, including `coHomeAppKeys`.
 * Layout uses this for every page so same-URL co-homes keep the current module chrome.
 */
export function coHomeAppKeysForPathname(pathname: string): IntegratorSubappKey[] {
  const normalized = normalizeQuickNavPath(pathname.split("?")[0] ?? pathname);
  const keys = new Set<IntegratorSubappKey>();
  for (const app of INTEGRATOR_SUBAPP_KEYS) {
    for (const item of NAV_BY_APP[app]) {
      const itemPath = normalizeQuickNavPath(item.to);
      if (normalized !== itemPath && !normalized.startsWith(`${itemPath}/`)) {
        continue;
      }
      keys.add(app);
      for (const extra of item.coHomeAppKeys ?? []) {
        keys.add(extra);
      }
    }
  }
  return [...keys];
}

/** Same-URL co-homes: one path listed on (or declared for) more than one module. */
export function listCoHomedNavDeclarations(): { to: string; homes: IntegratorSubappKey[] }[] {
  const byPath = new Map<string, Set<IntegratorSubappKey>>();
  const add = (to: string, key: IntegratorSubappKey): void => {
    const normalized = normalizeQuickNavPath(to);
    let homes = byPath.get(normalized);
    if (homes == null) {
      homes = new Set();
      byPath.set(normalized, homes);
    }
    homes.add(key);
  };
  for (const app of INTEGRATOR_SUBAPP_KEYS) {
    for (const item of NAV_BY_APP[app]) {
      add(item.to, app);
      for (const extra of item.coHomeAppKeys ?? []) {
        add(item.to, extra);
      }
    }
  }
  return [...byPath.entries()]
    .filter(([, homes]) => homes.size > 1)
    .map(([to, homes]) => ({
      to,
      homes: [...homes].sort((left, right) => left.localeCompare(right)),
    }))
    .sort((left, right) => left.to.localeCompare(right.to));
}

/**
 * Chrome module for a co-homed page.
 *
 * If the operator is already in a module that co-homes the page, keep that module.
 * If they arrive from somewhere else (Shipping, Go to… from another app), pick a
 * co-home they can access, preferring the route-owning module.
 */
export function resolveCoHomedLayoutApp(args: {
  pathname: string;
  routeApp: IntegratorSubappKey;
  lastApp: string;
  /** `null` while `/api/me` is still loading — do not guess a new module yet. */
  accessibleKeys: ReadonlySet<string> | null;
}): IntegratorSubappKey {
  const homes = coHomeAppKeysForPathname(args.pathname);
  if (homes.length <= 1) {
    return args.routeApp;
  }

  if (
    isIntegratorSubappKey(args.lastApp) &&
    homes.includes(args.lastApp) &&
    (args.accessibleKeys == null || args.accessibleKeys.has(args.lastApp))
  ) {
    return args.lastApp;
  }

  if (args.accessibleKeys == null) {
    return args.routeApp;
  }

  const preferred = [args.routeApp, ...homes.filter((key) => key !== args.routeApp)];
  for (const key of preferred) {
    if (args.accessibleKeys.has(key)) {
      return key;
    }
  }
  return args.routeApp;
}
